<template>
  <q-select
    ref="zubieSelect"
    :autocomplete="autocomplete"
    :autofocus="autofocus"
    :behavior="capacitorUseInput ? 'dialog' : behavior"
    :bg-color="capacitorPopup ? 'primary' : bgColor"
    :bottom-slots="bottomSlots"
    :clearable="capacitorPopup ? false : clearable"
    :dense="capacitorUseInput && capacitorPopup ? false : dense"
    :disable="disable"
    :display-value-sanitize="displayValueSanitize"
    :emit-value="emitValue"
    :error="error"
    :error-message="errorMessage"
    :fill-input="fillInput"
    :filled="filled"
    :for="forInput"
    :hide-bottom-space="capacitorPopup ? true : hideBottomSpace"
    :hide-dropdown-icon="hideDropdownIcon"
    :hide-selected="hideSelected"
    :hint="hint"
    :input-debounce="inputDebounce"
    :label="capacitorPopup && popupLabel ? popupLabel : label"
    :label-color="capacitorPopup ? 'grey-1' : undefined"
    :loading="loading"
    :map-options="mapOptions"
    :multiple="multiple"
    :option-label="optionLabel"
    :option-value="optionValue"
    :options="options"
    :options-sanitize="optionsSanitize"
    popup-content-style="margin-bottom: 54px;"
    :readonly="readonly"
    :rules="rules"
    :stack-label="stackLabel"
    :transition-hide="$q.platform.is.capacitor ? 'scale' : transitionHide"
    :transition-show="$q.platform.is.capacitor ? 'scale' : transitionShow"
    :use-chips="useChips"
    :use-input="useInput"
    :value="value"
    :virtual-scroll-slice-size="virtualScrollSliceSize"
    v-on="$listeners.filter ? { filter: filterFn } : {}"
    @add="$emit('add', $event)"
    @input="
      $emit('update:value', $event);
      $emit('input', $event);
    "
    @input-value="$emit('input-value', $event)"
    @new-value="onNewValue"
    @popup-hide="onPopupHide"
    @popup-show="onPopupShow"
    @remove="$emit('remove', $event)"
  >
    <template v-if="$scopedSlots['label'] || (capacitorUseInput && capacitorPopup)" v-slot:label="scope">
      <slot v-if="!capacitorUseInput" v-bind="scope" name="label" />
      <div v-else-if="capacitorPopup">{{ popupLabel }}</div>
    </template>
    <template
      v-if="(!capacitorPopup && $scopedSlots.prepend) || (capacitorPopup && capacitorUseInput)"
      v-slot:prepend="scope"
    >
      <slot v-if="!capacitorUseInput" v-bind="scope" name="prepend" />
      <div v-else class="absolute q-ml-xs text-grey-4">
        <q-icon class="q-mr-md" name="search" />
      </div>
    </template>
    <template v-if="$scopedSlots['selected-item'] || (capacitorUseInput && !useChips)" v-slot:selected-item="scope">
      <slot v-if="!capacitorUseInput" v-bind="scope" name="selected-item" />
      <div
        v-else
        :class="{
          hidden: capacitorPopup && !multiple,
          'self-start q-py-sm line-height-1 text-subtitle1 text-white': capacitorPopup,
        }"
      >
        {{ getLabel(scope) }}
      </div>
    </template>
    <template v-if="$q.platform.is.capacitor || $scopedSlots.option" v-slot:option="scope">
      <slot v-bind="scope" name="option">
        <q-item :class="{ 'bg-blue-2': useInput && scope.selected }" clickable @click="onItemClick(scope)">
          <q-item-section v-if="multiple" side>
            <q-checkbox :value="scope.selected" @input="onItemClick(scope)" />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ getLabel(scope) }}</q-item-label>
          </q-item-section>
        </q-item>
      </slot>
    </template>
    <template v-if="$scopedSlots['no-option']" v-slot:no-option="scope">
      <slot v-bind="scope" name="no-option" />
    </template>
    <div v-if="capacitorPopup" class="action-buttons fixed-bottom full-width">
      <q-separator />
      <div class="row justify-evenly items-center">
        <q-btn
          v-close-popup
          bg-color="white"
          class="action-button action-button__close q-py-sm q-px-sm"
          color="dark-grey"
          flat
          label="Close"
          rounded
          text-color="dark-grey"
        />
        <q-btn
          v-if="actionButtonText"
          class="action-button q-py-none q-px-sm"
          color="primary"
          :disable="disableActionButton"
          :label="actionButtonText"
          :loading="loading"
          rounded
          text-color="white"
          @click="performAction"
        />
      </div>
    </div>
  </q-select>
</template>

<script>
import { setTimeoutPromise } from 'src/services/setTimeout';

export default {
  name: 'ZubieSelect',
  props: {
    actionButtonText: String,
    autocomplete: String,
    autofocus: Boolean,
    behavior: String,
    bgColor: String,
    bottomSlots: Boolean,
    clearable: Boolean,
    closeOnInput: {
      type: Boolean,
      default: true,
    },
    dense: Boolean,
    disable: Boolean,
    disableActionButton: Boolean,
    displayValueSanitize: Boolean,
    emitValue: Boolean,
    error: {
      type: Boolean,
      default: null,
    },
    errorMessage: String,
    forceAutofocus: Boolean,
    filled: Boolean,
    fillInput: Boolean,
    for: String,
    hideBottomSpace: Boolean,
    hideDropdownIcon: Boolean,
    hideSelected: Boolean,
    hint: String,
    inputDebounce: Number,
    label: String,
    loading: Boolean,
    mapOptions: Boolean,
    multiple: Boolean,
    optionLabel: [String, Function],
    options: Array,
    optionValue: String,
    optionsSanitize: Boolean,
    popupLabel: String,
    readonly: Boolean,
    rules: Array,
    stackLabel: Boolean,
    transitionHide: String,
    transitionShow: String,
    useChips: Boolean,
    useInput: Boolean,
    value: [Array, Object],
    virtualScrollSliceSize: Number,
  },
  computed: {
    capacitorPopup() {
      return this.$q.platform.is.capacitor && this.popupOpen;
    },
    capacitorUseInput() {
      return this.$q.platform.is.capacitor && this.useInput;
    },
    forInput() {
      return this.for;
    },
  },
  data() {
    return {
      closeAfterLoaded: false,
      popupOpen: false,
    };
  },
  methods: {
    filter(...args) {
      return this.$refs.zubieSelect.filter(...args);
    },
    filterFn(value, update, abort) {
      this.$emit('filter', value, update, abort);
    },
    getLabel({ opt: option }) {
      return this.$refs.zubieSelect?.getOptionLabel(option);
    },
    async onItemClick({ opt: option }) {
      const optionValue = this.emitValue ? option[this.optionValue || 'value'] : option;

      let newValue;
      if (this.multiple) {
        newValue = this.value ? [...this.value] : [];
        const itemIndex = newValue.indexOf(optionValue);
        if (itemIndex > -1) {
          newValue.splice(itemIndex, 1);
        } else {
          newValue.push(optionValue);
        }
      } else {
        newValue = optionValue;
      }

      this.$emit('update:value', newValue);
      this.$emit('input', newValue);

      if (this.useInput && !this.multiple && this.closeOnInput) {
        this.delayedClose();
      }
    },
    onNewValue(...args) {
      this.$emit('new-value', ...args);
    },
    onPopupHide() {
      this.popupOpen = false;
      if (this.$q.platform.is.capacitor) {
        document.body.classList.remove('zubie-select', 'zubie-select--shown', 'zubie-select--ready');
      }
      this.closeAfterLoaded = false;
      this.$emit('popup-hide');
    },
    async onPopupShow() {
      this.popupOpen = true;

      if (this.$q.platform.is.capacitor) {
        document.body.classList.add('zubie-select');
        await setTimeoutPromise(250);
        document.body.classList.add('zubie-select--shown');
        await setTimeoutPromise(150);
        document.body.classList.add('zubie-select--ready');
        await this.$nextTick();
        const input = this.$el.querySelector('.q-select__dialog .q-field__input');
        input.focus();
        input.value = '';
      }

      this.$emit('popup-show');
    },
    async performAction() {
      this.$emit('action');
      await setTimeoutPromise(1);
      if (this.loading) {
        this.closeAfterLoaded = true;
      } else {
        this.delayedClose();
      }
    },
    async delayedClose() {
      await setTimeoutPromise(200);
      this.$refs.zubieSelect.hidePopup();
    },
    updateInputValue(value) {
      this.$refs.zubieSelect.updateInputValue(value);
    },
    hidePopup() {
      this.$refs.zubieSelect.hidePopup();
    },
  },
  async mounted() {
    if (this.forceAutofocus) {
      await this.$nextTick();
      this.$refs.zubieSelect.focus();
    }
  },
  watch: {
    loading(isLoading, wasLoading) {
      if (this.closeAfterLoaded && wasLoading && !isLoading) {
        this.delayedClose();
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
/deep/ .action-button__close .q-btn__content {
  color: $dark-grey;
}

.action-button {
  margin-bottom: env(safe-area-inset-bottom);
}
</style>

<style lang="stylus">
.capacitor .q-select .q-field__input {
  display: none;
}

.zubie-select {
  .q-field__prepend,
  .q-field__native {
    opacity: 0;
    transition: opacity 0.3s ease-out;
  }

  .q-field--dense .q-field__control-container {
    padding-top: 18px !important;
  }

  &.zubie-select--ready {
    .q-field__prepend,
    .q-field__native {
      opacity: 1;
    }
  }

  .action-buttons {
    display: none;
  }
}

/* Note: selector usage is super specific on purpose */
body.zubie-select.zubie-select--shown {
  .q-dialog__inner--minimized {
    padding: 0;
  }

  .q-dialog__inner {
    height: 100%;

    .q-select__dialog {
      border-radius: 0;
      height: 100%;
      max-height: 100% !important;
      max-width: 100% !important;
      width: 100% !important;
    }
  }

  .q-chip {
    background: white !important;
    color: black !important;
  }

  .action-buttons {
    display: block;
  }

  .q-select--with-chips {
    .q-field__native {
      display: block;
    }
  }

  .q-select--with-input {
    .q-field__prepend {
      padding: 0;
      position: relative;
      z-index: 1;
      align-self: flex-end;

      .q-icon {
        margin-right: 0;
        color: $grey-7;
      }
    }

    .q-field__label {
      transform: translateY(-40%) scale(0.75);
      right: calc(-100% / 3);
      transition: transform 0.36s cubic-bezier(0.4, 0, 0.2, 1), right 0.396s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .q-field__native {
      flex-direction: column;
      margin-bottom: 8px;
      padding: 0;

      .q-spinner {
        position: absolute;
        right: 6px;
        bottom: -1px;
      }
    }

    .q-field__input {
      display: block;
      height: auto;
      margin-top: 4px;
      padding: 8px 8px 8px 32px;
      width: 100%;
      font-size: 16px;
      line-height: 16px;
      background: white;
    }

    .q-field__append {
      position: relative;
      align-items: flex-end;
      padding: 0;
    }
  }
}
</style>
